import { useState, useEffect, useRef } from 'react'
import { X, Minus, Plus, Trash2, Send, ShoppingCart } from 'lucide-react'
import type { Cart } from '@/types'
import { CartItem } from './CartItem'
import { buildWhatsAppMessage, createWhatsAppURL } from '@/hooks/useCart'
import type { Language } from '@/types'

interface CartSheetProps {
  cart: Cart | null
  businessName: string
  whatsapp: string
  language: Language
  isOpen: boolean
  onClose: () => void
  onUpdateQuantity: (index: number, quantity: number) => void
  onRemoveItem: (index: number) => void
  onClear: () => void
}

export function CartSheet({
  cart,
  businessName,
  whatsapp,
  language,
  isOpen,
  onClose,
  onUpdateQuantity,
  onRemoveItem,
  onClear,
}: CartSheetProps) {
  const [note, setNote] = useState('')
  const [isSending, setIsSending] = useState(false)
  const sheetRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [startY, setStartY] = useState(0)
  const [currentY, setCurrentY] = useState(0)

  // Load note from cart when it changes
  useEffect(() => {
    if (cart?.note) {
      setNote(cart.note)
    }
  }, [cart])

  // Prevent body scroll when cart is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  // Handle swipe down to close
  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true)
    setStartY(e.touches[0].clientY)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return
    const y = e.touches[0].clientY
    setCurrentY(y - startY)
  }

  const handleTouchEnd = () => {
    if (!isDragging) return
    setIsDragging(false)

    // Close if dragged down more than 100px
    if (currentY > 100) {
      onClose()
    }
    setCurrentY(0)
  }

  const handleSendOrder = () => {
    if (!cart || cart.items.length === 0) return

    setIsSending(true)

    // Build WhatsApp message with note
    const message = buildWhatsAppMessage(
      { ...cart, note },
      businessName,
      language
    )
    const url = createWhatsAppURL(whatsapp, message)

    // Open WhatsApp
    window.open(url, '_blank')

    // Clear cart after sending
    setTimeout(() => {
      onClear()
      setNote('')
      setIsSending(false)
      onClose()
    }, 500)
  }

  const translations = {
    notePlaceholder: {
      fr: 'Instructions spéciales (allergies, préférences...)',
      ar: 'تعليمات خاصة (حساسية، تفضيلات...)',
      en: 'Special instructions (allergies, preferences...)'
    },
    orderButton: {
      fr: 'Commander sur WhatsApp',
      ar: 'اطلب عبر واتساب',
      en: 'Order on WhatsApp'
    },
    orderButtonSending: {
      fr: 'Envoi...',
      ar: 'جاري الإرسال...',
      en: 'Sending...'
    },
    emptyCart: {
      fr: 'Votre panier est vide',
      ar: 'سلة التسوق فارغة',
      en: 'Your cart is empty'
    },
    noteLabel: {
      fr: 'Note :',
      ar: 'ملاحظة :',
      en: 'Note:'
    },
    totalLabel: {
      fr: 'Total :',
      ar: 'المجموع :',
      en: 'Total:'
    },
    subtotalLabel: {
      fr: 'Sous-total :',
      ar: 'المجموع الفرعي :',
      en: 'Subtotal:'
    },
    close: {
      fr: 'Fermer',
      ar: 'إغلاق',
      en: 'Close'
    },
    yourCart: {
      fr: 'Votre Panier',
      ar: 'سلة التسوق',
      en: 'Your Cart'
    }
  }

  const t = (key: keyof typeof translations) => translations[key][language]

  const isEmpty = !cart || cart.items.length === 0
  const subtotal = cart?.subtotal || 0

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 animate-fade-in"
          onClick={onClose}
        />
      )}

      {/* Sheet */}
      <div
        ref={sheetRef}
        className={`
          fixed inset-x-0 md:inset-x-4 bottom-0 z-50 max-h-[85vh]
          bg-white rounded-t-3xl shadow-2xl
          flex flex-col
          transition-transform duration-300 ease-out
          ${isOpen ? 'translate-y-0' : 'translate-y-full'}
          ${isDragging ? 'transition-none' : ''}
        `}
        style={{
          transform: isDragging
            ? `translateY(${Math.min(currentY, 200)}px)`
            : isOpen
            ? 'translateY(0)'
            : 'translateY(100%)'
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Drag Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-12 h-1.5 bg-secondary/50 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-secondary/10">
          <h2 className="text-xl font-bold text-primary">
            {t('yourCart')}
          </h2>
          <div className="flex items-center gap-2">
            {!isEmpty && (
              <button
                onClick={onClear}
                className="px-3 py-1.5 text-sm text-red-500 hover:bg-red-50 rounded-lg transition-colors touch-feedback"
              >
                {language === 'fr' ? 'Vider' : language === 'ar' ? 'مسح' : 'Clear'}
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-secondary/20 transition-colors touch-feedback"
              aria-label="Close cart"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        {isEmpty ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center animate-fade-in">
            <div className="w-20 h-20 bg-secondary/30 rounded-full flex items-center justify-center mb-4">
              <ShoppingCart size={36} className="text-muted-foreground" />
            </div>
            <h3 className="text-xl font-semibold text-primary mb-2">
              {t('emptyCart')}
            </h3>
            <p className="text-muted-foreground mb-6">
              {language === 'fr' && 'Ajoutez des articles pour commencer'}
              {language === 'ar' && 'أضف عناصر للبدء'}
              {language === 'en' && 'Add items to get started'}
            </p>
            <button
              onClick={onClose}
              className="px-6 py-3 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors touch-feedback"
            >
              {t('close')}
            </button>
          </div>
        ) : (
          <>
            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {cart?.items.map((item, index) => (
                <CartItem
                  key={`${item.itemId}-${index}`}
                  item={item}
                  language={language}
                  onQuantityChange={(qty) => onUpdateQuantity(index, qty)}
                  onRemove={() => onRemoveItem(index)}
                />
              ))}
            </div>

            {/* Note Section */}
            <div className="p-4 border-t bg-secondary/10">
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                {t('noteLabel')}
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t('notePlaceholder')}
                rows={2}
                className="w-full px-3 py-2 rounded-xl border-2 border-secondary/30 bg-white resize-none focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors text-sm"
              />
            </div>

            {/* Footer */}
            <div className="p-4 border-t bg-white rounded-t-3xl shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
              {/* Subtotal */}
              <div className="flex items-center justify-between mb-3 text-sm text-muted-foreground">
                <span>{t('subtotalLabel')}</span>
                <span>{subtotal.toFixed(2)} MAD</span>
              </div>

              {/* Total */}
              <div className="flex items-center justify-between mb-4">
                <span className="text-lg font-semibold text-primary">
                  {t('totalLabel')}
                </span>
                <span className="text-2xl font-bold text-primary">
                  {subtotal.toFixed(2)} MAD
                </span>
              </div>

              {/* Order Button */}
              <button
                onClick={handleSendOrder}
                disabled={isSending}
                className={`
                  w-full flex items-center justify-center gap-2
                  py-4 px-6 rounded-xl
                  bg-[#25D366] text-white
                  hover:bg-[#20BA5A]
                  disabled:bg-gray-300 disabled:cursor-not-allowed
                  transition-all duration-200 touch-feedback
                  font-semibold text-lg shadow-lg hover:shadow-xl
                  ${isSending ? 'animate-pulse' : ''}
                `}
              >
                {isSending ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {t('orderButtonSending')}
                  </>
                ) : (
                  <>
                    <Send size={20} fill="currentColor" />
                    {t('orderButton')}
                  </>
                )}
              </button>

              {/* WhatsApp branding */}
              <p className="text-xs text-center text-muted-foreground mt-2">
                {language === 'fr' && 'Commande sécurisée via WhatsApp'}
                {language === 'ar' && 'طلب آمن عبر واتساب'}
                {language === 'en' && 'Secure order via WhatsApp'}
              </p>
            </div>
          </>
        )}
      </div>
    </>
  )
}
